import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { UpdateWorkflowStepsResponse, updateWorkflowSteps } from 'api/workflows';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { transformNodesToSteps } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { queryClient } from 'queries/queryClient';
import { workflowMutationKeys, workflowQueryKeys } from 'queries/workflow-query-keys';
import { GivebutterAxiosError } from 'types/error';

const useSaveWorkflowSteps = () => {
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const { nodes, setAutoSaveState } = useReactFlowStore(({ nodes, setAutoSaveState }) => ({
        nodes,
        setAutoSaveState,
    }));

    return useMutation<
        UpdateWorkflowStepsResponse,
        GivebutterAxiosError,
        NodeWithData[] | undefined
    >({
        mutationKey: workflowMutationKeys.workflowAutoSave(),
        mutationFn: newNodes => {
            const steps = transformNodesToSteps({
                nodes: newNodes ?? nodes,
                workflowId: workflow.id,
            });
            return updateWorkflowSteps(accountId, workflow.id, {
                steps,
                workflow_updated_at: workflow.last_step_updated_at ?? workflow.updated_at,
            });
        },
        onMutate: () => {
            setAutoSaveState('pending');
        },
        onSuccess: response => {
            setAutoSaveState('success');
            queryClient.setQueryData(
                workflowQueryKeys.workflowById(accountId, workflow.id),
                response.data.workflow,
            );
            queryClient.setQueryData(
                workflowQueryKeys.workflowStepsByIdWithFilters(accountId, workflow.id, {
                    version:
                        workflow.status !== 'draft'
                            ? workflow.current_version + 1
                            : workflow.current_version,
                }),
                response.data.steps,
            );
        },
        onError: e => {
            setAutoSaveState('error');
            error(e.response?.data.message || 'Unable to save Workflow.');
        },
    });
};

export { useSaveWorkflowSteps };
